import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { WeeklyTimesheet } from '../components/WeeklyTimesheet';

const DrafterPortal = ({ tab = 'dashboard' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(tab);
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkinNote, setCheckinNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync activeTab with URL prop
  useEffect(() => {
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [tab]);

  // Mock drafter data (would come from API)
  const drafter = {
    id: 'TM-001',
    name: 'Mike Rodriguez',
    role: 'Senior Drafter',
    type: '1099',
    points: 450,
    rate: 45,
    hoursThisWeek: 32,
    earningsThisMonth: 5400
  };

  const assignments = [
    {
      id: 'PRJ-2024-0042',
      name: 'Walmart Distribution Center - Atlanta',
      client: 'ABC Roofing Co.',
      dueDate: '2024-01-15',
      priority: 'high',
      status: 'in_progress',
      progress: 65,
      sheets: [
        { id: 'SD-A1', name: 'Roof Plan', status: 'complete' },
        { id: 'SD-A2', name: 'Details', status: 'in_progress' },
        { id: 'SD-A3', name: 'Sections', status: 'not_started' }
      ]
    },
    {
      id: 'PRJ-2024-0041',
      name: 'Amazon Fulfillment - Phoenix',
      client: 'XYZ Contractors',
      dueDate: '2024-01-20',
      priority: 'normal',
      status: 'pending',
      progress: 0,
      sheets: []
    },
    {
      id: 'PRJ-2024-0040',
      name: 'Target Distribution - Dallas',
      client: 'Premier Contractors',
      dueDate: '2024-01-25',
      priority: 'low',
      status: 'completed',
      progress: 100,
      sheets: [
        { id: 'SD-A1', name: 'Roof Plan', status: 'complete' },
        { id: 'SD-A2', name: 'Details', status: 'complete' }
      ]
    }
  ];

  // Handle tab change with URL navigation
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Navigate to proper URL
    if (newTab === 'dashboard') {
      navigate('/drafter');
    } else {
      navigate(`/drafter/${newTab}`);
    }
  };

  const handleCheckin = async () => {
    setLoading(true);
    try {
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API
      setCheckedIn(true);
    } catch (err) {
      setError('Failed to check in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="drafter-portal">
      {/* Header */}
      <div className="portal-header">
        <div className="header-info">
          <h1>Welcome, {drafter.name}</h1>
          <p>{drafter.role} - {drafter.type} Contractor</p>
        </div>
        <div className="header-stats">
          <div className="quick-stat">
            <span className="value">{drafter.hoursThisWeek}h</span>
            <span className="label">This Week</span>
          </div>
          <div className="quick-stat">
            <span className="value">${drafter.earningsThisMonth.toLocaleString()}</span>
            <span className="label">This Month</span>
          </div>
          <div className="quick-stat">
            <span className="value">{drafter.points}</span>
            <span className="label">Points</span>
          </div>
        </div>
      </div>

      {/* Daily Check-in Banner */}
      {!checkedIn && (
        <div className="checkin-banner">
          <div className="checkin-content">
            <span className="checkin-icon">&#9728;</span>
            <div className="checkin-text">
              <h3>Good morning! Time to check in</h3>
              <p>Let us know what you're working on today</p>
            </div>
          </div>
          <div className="checkin-form">
            <input
              type="text"
              placeholder="What are you working on today?"
              value={checkinNote}
              onChange={(e) => setCheckinNote(e.target.value)}
            />
            <button
              className="btn primary"
              onClick={handleCheckin}
              disabled={loading}
            >
              {loading ? 'Checking in...' : 'Check In'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="tab-navigation">
        <button
          className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleTabChange('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`tab-btn ${activeTab === 'assignments' ? 'active' : ''}`}
          onClick={() => handleTabChange('assignments')}
        >
          My Work
        </button>
        <button
          className={`tab-btn ${activeTab === 'standards' ? 'active' : ''}`}
          onClick={() => handleTabChange('standards')}
        >
          Standards
        </button>
        <button
          className={`tab-btn ${activeTab === 'time' ? 'active' : ''}`}
          onClick={() => handleTabChange('time')}
        >
          Time Log
        </button>
        <button
          className={`tab-btn ${activeTab === 'rewards' ? 'active' : ''}`}
          onClick={() => handleTabChange('rewards')}
        >
          Rewards
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'dashboard' && (
          <DrafterDashboard drafter={drafter} onNavigate={handleTabChange} />
        )}
        {activeTab === 'assignments' && (
          <AssignmentsTab />
        )}
        {activeTab === 'standards' && <StandardsTab />}
        {activeTab === 'time' && <TimeLogTab drafter={drafter} />}
        {activeTab === 'rewards' && <RewardsTab points={drafter.points} />}
      </div>
    </div>
  );
};

// Dashboard Tab - Now uses real API data
const DrafterDashboard = ({ drafter, onNavigate }) => {
  const [stats, setStats] = useState({ active_projects: 0, hours_this_week: 0, pending_reviews: 0 });
  const [assignments, setAssignments] = useState([]);
  const [recentEntries, setRecentEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch('/api/drafter/dashboard', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data.stats || {});
          setAssignments(data.assignments || []);
          setRecentEntries(data.recent_entries || []);
        }
      } catch (err) {
        console.error('Failed to fetch dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  const activeAssignments = assignments.filter(a =>
    a.project_status === 'in_progress' || a.project_status === 'review'
  );
  const queuedAssignments = assignments.filter(a =>
    a.project_status === 'queued' || a.project_status === 'intake'
  );

  if (loading) {
    return <div className="loading-state">Loading dashboard...</div>;
  }

  return (
    <div className="drafter-dashboard">
      {/* Stats Row */}
      <div className="stats-row" style={{ marginBottom: '1.5rem' }}>
        <div className="stat-box">
          <span className="stat-value">{stats.active_projects || 0}</span>
          <span className="stat-label">Active Projects</span>
        </div>
        <div className="stat-box">
          <span className="stat-value">{stats.hours_this_week?.toFixed(1) || 0}</span>
          <span className="stat-label">Hours This Week</span>
        </div>
        <div className="stat-box">
          <span className="stat-value">{stats.hours_this_month?.toFixed(1) || 0}</span>
          <span className="stat-label">Hours This Month</span>
        </div>
        <div className="stat-box">
          <span className="stat-value">{stats.pending_reviews || 0}</span>
          <span className="stat-label">In Review</span>
        </div>
      </div>

      {/* Current Work */}
      <div className="section">
        <div className="section-header">
          <h2>Current Work</h2>
          <button className="btn secondary small" onClick={() => onNavigate('assignments')}>
            View All
          </button>
        </div>
        {activeAssignments.length === 0 ? (
          <div className="empty-state">
            <p>No active assignments. Check pending queue below.</p>
          </div>
        ) : (
          <div className="assignment-cards">
            {activeAssignments.map(project => (
              <div key={project.id} className="assignment-card active">
                <div className="card-header">
                  <span className="project-id">{project.project_number}</span>
                  <span className={`role-badge ${project.role}`} style={{ textTransform: 'capitalize' }}>
                    {project.role?.replace('_', ' ')}
                  </span>
                </div>
                <h3>{project.project_name}</h3>

                {project.estimated_hours && (
                  <div className="progress-section">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${Math.min(project.progress_percent || 0, 100)}%`,
                          backgroundColor: (project.progress_percent || 0) > 100 ? '#dc2626' : '#2563eb'
                        }}
                      ></div>
                    </div>
                    <span className="progress-text">
                      {project.actual_hours?.toFixed(1) || 0} / {project.estimated_hours} hrs
                    </span>
                  </div>
                )}

                {project.due_date && (
                  <div className="due-date">
                    Due: {new Date(project.due_date).toLocaleDateString()}
                  </div>
                )}

                <div className="card-actions">
                  <button className="btn primary" onClick={() => onNavigate('time')}>
                    Log Time
                  </button>
                  <button className="btn secondary" onClick={() => onNavigate('assignments')}>
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Queue */}
      <div className="section">
        <h2>Pending Queue</h2>
        {queuedAssignments.length === 0 ? (
          <div className="empty-state">
            <p>No pending assignments. Nice work!</p>
          </div>
        ) : (
          <div className="pending-list">
            {queuedAssignments.map(project => (
              <div key={project.id} className="pending-item">
                <div className="project-info">
                  <span className="project-id">{project.project_number}</span>
                  <span className="project-name">{project.project_name}</span>
                </div>
                <div className="project-meta">
                  <span className="role" style={{ textTransform: 'capitalize' }}>
                    {project.role?.replace('_', ' ')}
                  </span>
                  {project.due_date && (
                    <span className="due">Due: {new Date(project.due_date).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Time Entries */}
      {recentEntries.length > 0 && (
        <div className="section">
          <h2>Recent Time Entries</h2>
          <table className="data-table" style={{ fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Project</th>
                <th>Hours</th>
                <th>Task</th>
              </tr>
            </thead>
            <tbody>
              {recentEntries.slice(0, 5).map(entry => (
                <tr key={entry.id}>
                  <td>{new Date(entry.date).toLocaleDateString()}</td>
                  <td>{entry.project_number}</td>
                  <td>{entry.hours?.toFixed(1)}</td>
                  <td style={{ textTransform: 'capitalize' }}>{entry.task_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Assignments Tab (My Work) - Now uses real API data
const AssignmentsTab = () => {
  const [filter, setFilter] = useState('all');
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const response = await fetch('/api/assignments/user/me', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAssignments(data.assignments || []);
        }
      } catch (err) {
        console.error('Failed to fetch assignments:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAssignments();
  }, []);

  const filteredAssignments = assignments.filter(a => {
    if (filter === 'all') return true;
    return a.project_status === filter || a.status === filter;
  });

  const handleOpenProject = (project) => {
    setSelectedProject(project);
  };

  if (loading) {
    return <div className="loading-state">Loading assignments...</div>;
  }

  return (
    <div className="assignments-tab">
      <div className="assignments-header">
        <h2>My Assignments</h2>
        <div className="filter-buttons">
          <button
            className={`btn small ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({assignments.length})
          </button>
          <button
            className={`btn small ${filter === 'in_progress' ? 'active' : ''}`}
            onClick={() => setFilter('in_progress')}
          >
            In Progress ({assignments.filter(a => a.project_status === 'in_progress').length})
          </button>
          <button
            className={`btn small ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active ({assignments.filter(a => a.status === 'active').length})
          </button>
        </div>
      </div>

      {selectedProject ? (
        <ProjectDetailView
          project={selectedProject}
          onBack={() => setSelectedProject(null)}
        />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Project #</th>
              <th>Project Name</th>
              <th>Role</th>
              <th>Due Date</th>
              <th>Hours</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAssignments.length === 0 ? (
              <tr>
                <td colSpan={7} className="empty-row">
                  No assignments match this filter.
                </td>
              </tr>
            ) : (
              filteredAssignments.map(assignment => (
                <tr key={assignment.id}>
                  <td><code>{assignment.project_number}</code></td>
                  <td>{assignment.project_name}</td>
                  <td style={{ textTransform: 'capitalize' }}>{assignment.role?.replace('_', ' ')}</td>
                  <td>{assignment.due_date ? new Date(assignment.due_date).toLocaleDateString() : '-'}</td>
                  <td>
                    {assignment.actual_hours?.toFixed(1) || 0}
                    {assignment.estimated_hours ? ` / ${assignment.estimated_hours}` : ''}
                  </td>
                  <td>
                    <span className={`status-badge ${assignment.project_status || assignment.status}`}>
                      {(assignment.project_status || assignment.status)?.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn small primary"
                      onClick={() => handleOpenProject(assignment)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Project Detail View (shown when clicking Open on an assignment)
const ProjectDetailView = ({ project, onBack }) => {
  const [uploading, setUploading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [markedComplete, setMarkedComplete] = useState(false);
  const fileInputRef = useRef(null);

  // Assignments from /api/assignments/user/me carry project_id; fall back to id
  const projectId = project.project_id || project.id;
  const sheets = project.sheets || [];

  const handleFileSelected = async (e) => {
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('taxonomy_code', '501'); // Shop Drawing
      formData.append('taxonomy_category', 'GEOMETRY');
      formData.append('step_name', 'Shop Drawing Upload');

      const response = await fetch(`/api/projects/${projectId}/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        alert(`Uploaded: ${file.name}`);
      } else {
        alert(data.error || 'Upload failed. Please try again.');
      }
    } catch (err) {
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      input.value = '';
    }
  };

  const handleMarkComplete = async () => {
    setMarking(true);
    try {
      const response = await fetch(`/api/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ status: 'completed' })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMarkedComplete(true);
      } else {
        alert(data.error || 'Failed to mark project complete. Please try again.');
      }
    } catch (err) {
      alert('Failed to mark project complete. Please try again.');
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="project-detail-view">
      <div className="detail-header">
        <button className="btn secondary" onClick={onBack}>
          Back to Assignments
        </button>
        <h2>{project.name}</h2>
      </div>

      <div className="detail-content">
        <div className="detail-info">
          <div className="info-row">
            <span className="label">Project ID:</span>
            <span className="value"><code>{project.id}</code></span>
          </div>
          <div className="info-row">
            <span className="label">Client:</span>
            <span className="value">{project.client}</span>
          </div>
          <div className="info-row">
            <span className="label">Due Date:</span>
            <span className="value">{project.dueDate}</span>
          </div>
          <div className="info-row">
            <span className="label">Priority:</span>
            <span className={`priority-badge ${project.priority}`}>{project.priority}</span>
          </div>
          <div className="info-row">
            <span className="label">Progress:</span>
            <div className="progress-bar" style={{ width: '200px', display: 'inline-block' }}>
              <div className="progress-fill" style={{ width: `${project.progress}%` }}></div>
            </div>
            <span style={{ marginLeft: '8px' }}>{project.progress}%</span>
          </div>
        </div>

        <div className="sheets-section">
          <h3>Sheets</h3>
          {sheets.length === 0 ? (
            <p>No sheets created yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sheet ID</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sheets.map(sheet => (
                  <tr key={sheet.id}>
                    <td><code>{sheet.id}</code></td>
                    <td>{sheet.name}</td>
                    <td>
                      <span className={`status-badge ${sheet.status}`}>
                        {sheet.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <button className="btn small" disabled title="Coming soon">Edit</button>
                      <button className="btn small secondary" style={{ marginLeft: '8px' }} disabled title="Coming soon">
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="actions-section">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
          <button
            className="btn primary"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Sheet'}
          </button>
          <button className="btn secondary" disabled title="Coming soon">Download All Files</button>
          <button
            className="btn secondary"
            onClick={handleMarkComplete}
            disabled={marking || markedComplete}
          >
            {markedComplete ? 'Completed' : marking ? 'Marking...' : 'Mark Complete'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Standards Tab
const StandardsTab = () => {
  const [activeSection, setActiveSection] = useState('naming');

  return (
    <div className="standards-tab">
      <div className="standards-nav">
        <button
          className={activeSection === 'naming' ? 'active' : ''}
          onClick={() => setActiveSection('naming')}
        >
          File Naming
        </button>
        <button
          className={activeSection === 'layers' ? 'active' : ''}
          onClick={() => setActiveSection('layers')}
        >
          Layer Standards
        </button>
        <button
          className={activeSection === 'templates' ? 'active' : ''}
          onClick={() => setActiveSection('templates')}
        >
          Templates
        </button>
        <button
          className={activeSection === 'checklist' ? 'active' : ''}
          onClick={() => setActiveSection('checklist')}
        >
          QC Checklist
        </button>
      </div>

      <div className="standards-content">
        {activeSection === 'naming' && (
          <div className="naming-standards">
            <h2>File Naming Convention</h2>

            <div className="standard-card">
              <h3>SHOPDRAWINGS.IO Format</h3>
              <code className="code-block">
                [CLASS]-[CLIENT]-[YY]-[BASE#]-[SPEC]-R[REV].[ext]
              </code>

              <h4>Example:</h4>
              <code className="example">502-ADM-25-0004-071000-R0.dwg</code>
            </div>

            <div className="standard-card">
              <h3>Taxonomy Codes (CLASS)</h3>
              <table className="codes-table">
                <tbody>
                  <tr><td><code>101</code></td><td>Scope of Work</td></tr>
                  <tr><td><code>103</code></td><td>Specification</td></tr>
                  <tr><td><code>200</code></td><td>Construction Drawings</td></tr>
                  <tr><td><code>201</code></td><td>Taper Plan</td></tr>
                  <tr><td><code>290</code></td><td>Misc Input</td></tr>
                  <tr><td><code>301</code></td><td>NOA / Assembly Letter</td></tr>
                  <tr><td><code>302</code></td><td>Cut Sheet / Product Data</td></tr>
                  <tr><td><code>501</code></td><td>Shop Drawing</td></tr>
                  <tr><td><code>502</code></td><td>CAD DWG</td></tr>
                </tbody>
              </table>
            </div>

            <div className="standard-card">
              <h3>Revision Format</h3>
              <p>R0, R1, R2, R3... (R0 = initial issue)</p>

              <h4>Rules:</h4>
              <ul>
                <li>3-letter uppercase client code (e.g., ADM, WMT)</li>
                <li>2-digit year code (e.g., 25 for 2025)</li>
                <li>4-digit job number with leading zeros (e.g., 0001)</li>
                <li>6-digit CSI code from Division 07</li>
                <li>Revision starts at R0 for initial issue</li>
              </ul>
            </div>
          </div>
        )}

        {activeSection === 'layers' && (
          <div className="layer-standards">
            <h2>CAD Layer Standards</h2>

            <table className="data-table">
              <thead>
                <tr>
                  <th>Layer Name</th>
                  <th>Color</th>
                  <th>Linetype</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>ROOF-MEMBRANE</code></td>
                  <td><span className="color-swatch cyan"></span> Cyan</td>
                  <td>Continuous</td>
                  <td>Roof membrane outline</td>
                </tr>
                <tr>
                  <td><code>ROOF-INSULATION</code></td>
                  <td><span className="color-swatch yellow"></span> Yellow</td>
                  <td>Continuous</td>
                  <td>Insulation areas</td>
                </tr>
                <tr>
                  <td><code>ROOF-DECK</code></td>
                  <td><span className="color-swatch white"></span> White</td>
                  <td>Continuous</td>
                  <td>Deck outline</td>
                </tr>
                <tr>
                  <td><code>ROOF-DRAIN</code></td>
                  <td><span className="color-swatch blue"></span> Blue</td>
                  <td>Continuous</td>
                  <td>Drains and scuppers</td>
                </tr>
                <tr>
                  <td><code>ROOF-EDGE</code></td>
                  <td><span className="color-swatch red"></span> Red</td>
                  <td>Continuous</td>
                  <td>Edge conditions</td>
                </tr>
                <tr>
                  <td><code>ROOF-PENETRATION</code></td>
                  <td><span className="color-swatch magenta"></span> Magenta</td>
                  <td>Continuous</td>
                  <td>Penetrations, curbs</td>
                </tr>
                <tr>
                  <td><code>ROOF-DIM</code></td>
                  <td><span className="color-swatch green"></span> Green</td>
                  <td>Continuous</td>
                  <td>Dimensions</td>
                </tr>
                <tr>
                  <td><code>ROOF-TEXT</code></td>
                  <td><span className="color-swatch white"></span> White</td>
                  <td>Continuous</td>
                  <td>Text and notes</td>
                </tr>
                <tr>
                  <td><code>XREF</code></td>
                  <td><span className="color-swatch gray"></span> Gray (8)</td>
                  <td>Continuous</td>
                  <td>External references</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {activeSection === 'templates' && (
          <div className="templates">
            <h2>Drawing Templates</h2>

            <div className="template-grid">
              <div className="template-card">
                <span className="template-icon">&#128208;</span>
                <h4>Roof Plan Template</h4>
                <p>Standard roof plan with title block</p>
                <button className="btn small primary" disabled title="Coming soon">Download .dwg</button>
              </div>
              <div className="template-card">
                <span className="template-icon">&#128207;</span>
                <h4>Detail Sheet Template</h4>
                <p>Multiple detail viewports</p>
                <button className="btn small primary" disabled title="Coming soon">Download .dwg</button>
              </div>
              <div className="template-card">
                <span className="template-icon">&#128200;</span>
                <h4>Taper Plan Template</h4>
                <p>Drainage plan layout</p>
                <button className="btn small primary" disabled title="Coming soon">Download .dwg</button>
              </div>
              <div className="template-card">
                <span className="template-icon">&#128203;</span>
                <h4>Title Block</h4>
                <p>Standard EDA title block</p>
                <button className="btn small primary" disabled title="Coming soon">Download .dwg</button>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'checklist' && (
          <div className="qc-checklist">
            <h2>Quality Control Checklist</h2>

            <div className="checklist-section">
              <h3>Before Submission</h3>
              <div className="checklist-items">
                <label><input type="checkbox" /> All layers correct per standards</label>
                <label><input type="checkbox" /> File naming follows convention</label>
                <label><input type="checkbox" /> Title block filled out completely</label>
                <label><input type="checkbox" /> Dimensions verified and complete</label>
                <label><input type="checkbox" /> Scale correct in all viewports</label>
                <label><input type="checkbox" /> Spell check completed</label>
                <label><input type="checkbox" /> All XREFs attached correctly</label>
                <label><input type="checkbox" /> Assembly details match spec</label>
                <label><input type="checkbox" /> Edge details match manufacturer</label>
                <label><input type="checkbox" /> PDF created and reviewed</label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Time Log Tab - Uses API-backed WeeklyTimesheet component
const TimeLogTab = ({ drafter }) => {
  const [stats, setStats] = useState({ hours_this_week: 0, hours_this_month: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('/api/drafter/stats', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="time-log-tab">
      {/* Summary Stats */}
      <div className="time-summary" style={{ marginBottom: '1.5rem' }}>
        <div className="summary-stats">
          <div className="stat-box">
            <span className="value">{stats.hours_this_week?.toFixed(1) || 0}h</span>
            <span className="label">This Week</span>
          </div>
          <div className="stat-box">
            <span className="value">{stats.hours_this_month?.toFixed(1) || 0}h</span>
            <span className="label">This Month</span>
          </div>
          <div className="stat-box">
            <span className="value">${((stats.hours_this_month || 0) * (drafter?.rate || 45)).toLocaleString()}</span>
            <span className="label">Earnings (Month)</span>
          </div>
        </div>
      </div>

      {/* Weekly Timesheet Component (API-backed) */}
      <WeeklyTimesheet />
    </div>
  );
};

// Rewards Tab
const RewardsTab = ({ points }) => {
  const [redeeming, setRedeeming] = useState(null);
  const [memberId, setMemberId] = useState(null);
  const [balance, setBalance] = useState(points);

  // Resolve the current user's team member record (id + live points balance)
  useEffect(() => {
    const fetchMember = async () => {
      try {
        const headers = { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` };
        const meResponse = await fetch('/api/auth/me', { headers });
        if (!meResponse.ok) return;
        const me = await meResponse.json();

        const teamResponse = await fetch('/api/team', { headers });
        if (!teamResponse.ok) return;
        const team = await teamResponse.json();

        const member = (team.members || []).find(m => m.platform_user_id === me.user?.id);
        if (member) {
          setMemberId(member.id);
          if (typeof member.reward_points === 'number') {
            setBalance(member.reward_points);
          }
        }
      } catch (err) {
        console.error('Failed to fetch member info:', err);
      }
    };
    fetchMember();
  }, []);

  const rewards = [
    { id: 'wawa-10', name: '$10 Wawa Gift Card', points: 100, icon: 'Coffee' },
    { id: 'wawa-25', name: '$25 Wawa Gift Card', points: 250, icon: 'Drink' },
    { id: 'amazon-25', name: '$25 Amazon Gift Card', points: 250, icon: 'Package' },
    { id: 'half-day', name: 'Half Day Friday', points: 500, icon: 'Palm' },
    { id: 'lunch', name: 'Free Team Lunch', points: 500, icon: 'Pizza' },
    { id: 'phillies', name: 'Phillies Tickets (2)', points: 1000, icon: 'Baseball' },
  ];

  const history = [
    { date: '2024-01-05', type: 'earned', points: 50, reason: 'Completed project on time' },
    { date: '2024-01-03', type: 'redeemed', points: -100, reason: '$10 Wawa Gift Card' },
    { date: '2024-01-01', type: 'earned', points: 25, reason: 'Monthly team bonus' },
    { date: '2023-12-28', type: 'earned', points: 100, reason: 'Quality bonus - zero revisions' },
    { date: '2023-12-20', type: 'earned', points: 75, reason: 'Early delivery bonus' },
  ];

  const handleRedeem = async (reward) => {
    if (balance < reward.points || !memberId) return;

    setRedeeming(reward.id);
    try {
      const response = await fetch('/api/rewards/redeem', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          member_id: memberId,
          points_required: reward.points,
          reward_name: reward.name,
          reward_type: reward.id
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        if (typeof data.new_balance === 'number') {
          setBalance(data.new_balance);
        }
        alert(`Successfully redeemed: ${reward.name}! Check your email for details.`);
      } else {
        alert(data.error || 'Failed to redeem reward. Please try again.');
      }
    } catch (err) {
      alert('Failed to redeem reward. Please try again.');
    } finally {
      setRedeeming(null);
    }
  };

  return (
    <div className="rewards-tab">
      {/* Points Balance */}
      <div className="points-banner">
        <div className="points-display">
          <span className="points-icon">Points</span>
          <span className="points-value">{balance}</span>
          <span className="points-label">Available</span>
        </div>
      </div>

      {/* Available Rewards */}
      <div className="section">
        <h2>Available Rewards</h2>
        <div className="rewards-grid">
          {rewards.map(reward => (
            <div
              key={reward.id}
              className={`reward-card ${balance >= reward.points ? 'available' : 'locked'}`}
            >
              <span className="reward-icon">{reward.icon}</span>
              <h4>{reward.name}</h4>
              <span className="reward-points">{reward.points} pts</span>
              <button
                className="btn small primary"
                disabled={balance < reward.points || !memberId || redeeming === reward.id}
                title={!memberId ? 'Account not linked to a team member' : undefined}
                onClick={() => handleRedeem(reward)}
              >
                {redeeming === reward.id ? 'Redeeming...' :
                 balance >= reward.points ? 'Redeem' : 'Not enough points'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Points History */}
      <div className="section">
        <h2>Points History</h2>
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Points</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {history.map((item, i) => (
              <tr key={i}>
                <td>{item.date}</td>
                <td>
                  <span className={`type-badge ${item.type}`}>
                    {item.type}
                  </span>
                </td>
                <td className={item.points > 0 ? 'positive' : 'negative'}>
                  {item.points > 0 ? '+' : ''}{item.points}
                </td>
                <td>{item.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DrafterPortal;
